<template>
  <div class="catalog-slider-fi">
    <div
      :key="refreshCount"
      :class="{
        'catalog-slider-fi__container': true,
        'catalog-slider-fi__container--visible': true,
        'catalog-slider-fi__container--single-slide': thumbnailsData.length === 1,
        'catalog-slider-fi__container--duo-slide': thumbnailsData.length === 2,
      }"
    >
      <carousel-fi
        ref="carousel"
        class="catalog-slider-fi__carousel"
        :settings="!isMD ? mainSettings : mainSettingsMD"
        :items="thumbnailsData"
        :no-arrows="!isMD"
        :thumbnail-carousel-ref="thumbnailCarouselRef"
        theme="catalog-slider"
        @swipe="notifySwipeToCatalogSlides"
      >
        <carousel-slide-fi
          v-for="(item, index) in thumbnailsData"
          :key="index"
          theme="catalog-slider"
        >
          <catalog-slider-item-fi
            ref="catalogItem"
            :item="item"
            :item-name="itemName"
            :zoomable="isZoomable && !item.videoId"
            :eager="index === 0"
            :root-category="catalogData?.RootCategory"
            @zoom="openZoom"
          />
        </carousel-slide-fi>
      </carousel-fi>

      <div class="catalog-slider-fi__thumbnail-wrapper">
        <carousel-fi
          ref="thumbnails"
          class="catalog-slider-fi__carousel catalog-slider-fi__carousel--thumbnail"
          :settings="thumbnailSettings"
          theme="catalog-slider-thumbnail"
          no-arrows
        >
          <carousel-slide-fi
            v-for="(item, index) in carouselThumbnails"
            :key="index"
            theme="catalog-slider-thumbnail"
          >
            <catalog-slider-item-fi
              :item="item"
              :is-thumbnail="true"
              :eager="index === 0"
              :root-category="catalogData?.RootCategory"
            />
          </carousel-slide-fi>
        </carousel-fi>
      </div>
    </div>
    <dialog-modal-fi
      v-if="isZoomable"
      ref="zoom"
      class="catalog-slider-fi__carousel-zoom"
      lazy
      full-screen
      @close="destroyZoom"
    >
      <template #header>
        <div class="catalog-slider-fi__carousel-zoom__header">
          <div class="catalog-slider-fi__carousel-zoom__header__subtitle">{{ headerSubtitle }}</div>
          <div class="catalog-slider-fi__carousel-zoom__header__title">{{ headerTitle }}</div>
        </div>
      </template>
      <template #body>
        <div class="catalog-slider-fi__carousel-zoom-wrapper">
          <carousel-fi
            ref="carouselZoom"
            class="catalog-slider-fi__carousel catalog-slider-fi__carousel-zoom"
            :settings="{ ...(!isMD ? mainSettings : mainSettingsMD), autoHeight: true }"
            :items="thumbnailsData"
            :no-arrows="!isMD"
            theme="catalog-slider"
            @swipe="notifySwipeToZoomCatalogSlides"
          >
            <carousel-slide-fi
              v-for="(item, index) in thumbnailsData"
              :key="index"
              theme="catalog-slider"
            >
              <catalog-slider-item-fi
                ref="zoomCatalogItem"
                :item="item"
                :item-name="itemName"
                is-zoom
              />
            </carousel-slide-fi>
          </carousel-fi>
        </div>

        <div class="catalog-slider-fi__thumbnail-wrapper catalog-slider-fi__zoom-thumbnail-wrapper">
          <carousel-fi
            ref="thumbnailsZoom"
            :class="{
              'catalog-slider-fi__carousel': true,
              'catalog-slider-fi__carousel--thumbnail': true,
              'catalog-slider-fi__carousel--thumbnail-zoom': true,
              'catalog-slider-fi__carousel--single': carouselThumbnails.length === 1,
            }"
            :settings="thumbnailSettingsZoom"
            theme="catalog-slider-thumbnail"
            no-arrows
          >
            <carousel-slide-fi
              v-for="(item, index) in carouselThumbnails"
              :key="index"
              theme="catalog-slider-thumbnail"
            >
              <catalog-slider-item-fi
                :item="item"
                :is-thumbnail="true"
              />
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </template>
    </dialog-modal-fi>
  </div>
</template>

<script setup lang="ts">
import { CarouselFi, CarouselSlideFi, DialogModalFi } from 'atoms';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import CatalogSliderItemFi from '@/components/catalog-slider-fi/CatalogSliderItemFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useCatalog } from '@/composables/catalog';
import { useCatalogDataHelper } from '@/composables/catalogDataHelper';
import { useFields } from '@/composables/fields';
import { useDict } from '@/composables/fischerPlugin';

interface CatalogTextList {
  Key?: string | null;
  Value?: string | null;
  AttributeKey?: string | null;
}

interface CatalogData {
  ProductId?: string | null;
  Number?: string | null;
  RootCategory?: string | null;
  TextList?: null | CatalogTextList[];
  Media?: null | any[];
}

interface Fields {
  enableZoom?: {
    value: boolean;
  };
}

interface Props {
  catalogData?: CatalogData | null;
  itemName?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  catalogData: null,
  itemName: null,
});

const { isMD } = useBreakpoints();
const { getFamilyThumbnails } = useCatalog();
const { textListValue } = useCatalogDataHelper();
const dict = useDict();
const fields = useFields<Fields>();

const carousel = useTemplateRef<InstanceType<typeof CarouselFi>>('carousel');
const thumbnails = useTemplateRef<InstanceType<typeof CarouselFi>>('thumbnails');
const zoom = useTemplateRef<InstanceType<typeof DialogModalFi>>('zoom');
const carouselZoom = useTemplateRef<InstanceType<typeof CarouselFi>>('carouselZoom');
const thumbnailsZoom = useTemplateRef<InstanceType<typeof CarouselFi>>('thumbnailsZoom');
const catalogItem = useTemplateRef<InstanceType<typeof CatalogSliderItemFi>[]>('catalogItem');
const zoomCatalogItem =
  useTemplateRef<InstanceType<typeof CatalogSliderItemFi>[]>('zoomCatalogItem');
const thumbnailCarouselRef = ref<InstanceType<typeof CarouselFi> | null>(null);

// Type changes do not work in breakpoint settings
const mainSettings = {
  rewind: true,
  pagination: true,
  gap: '24px',
};

const mainSettingsMD = {
  rewind: true,
  pagination: true, // we have pagination here too and just hide it to be consistent when re-hydrating
  type: 'fade',
  gap: '24px',
};

const refreshCount = ref(0);
const isMounted = ref(false);
const isZoomable = computed(() => {
  return isMD.value && fields.enableZoom?.value;
});
const isSeriesPage = computed(() => Boolean(props.catalogData?.Number));

const thumbnailsData = computed(() => {
  return getFamilyThumbnails(props.catalogData);
});

const headerSubtitle = computed(() => {
  if (isSeriesPage.value) {
    return textListValue(props.catalogData?.TextList as any, 'DOKBSTMARKCLAIM');
  }

  const artNr = props.catalogData?.ProductId && Number(props.catalogData.ProductId);
  if (artNr) {
    return `${dict.get('product-List.Table.Article-Number')} ${artNr}`;
  }

  return textListValue(props.catalogData?.TextList as any, 'KAT_POS');
});

const headerTitle = computed(() => {
  if (isSeriesPage.value) {
    return textListValue(props.catalogData?.TextList as any, 'ARTIKELLANG');
  }

  const productName = textListValue(props.catalogData?.TextList as any, 'ECOMLANG');
  if (productName) {
    return productName;
  }

  return textListValue(props.catalogData?.TextList as any, 'BEZEICHNUNG');
});

const carouselThumbnails = computed(() => {
  if (isMounted.value || thumbnailsData.value.length < 3) {
    return thumbnailsData.value;
  }
  // center first item
  const carouselThumbnailsArray = thumbnailsData.value.slice();
  const lastItem = carouselThumbnailsArray.pop();
  if (lastItem) {
    carouselThumbnailsArray.unshift(lastItem);
  }
  return carouselThumbnailsArray;
});

const thumbnailSettings = computed(() => {
  const settings: any = {
    type: 'loop',
    isNavigation: true,
    perPage: 3,
    perMove: 1,
    focus: 'center',
    gap: IS_SSR ? undefined : '24px',
  };

  const { length } = thumbnailsData.value;
  if (length < 3) {
    if (length === 1) {
      settings.padding = {
        right: '33.33%',
        left: '33.33%',
      };
      settings.gap = '100%';
    } else {
      settings.gap = '33.33%';
    }
    settings.perPage = length;
    settings.focus = 0;
  }

  return settings;
});

const thumbnailSettingsZoom = computed(() => {
  return {
    ...thumbnailSettings.value,
    type: 'slide',
    padding: undefined,
    autoWidth: true,
    rewind: false,
    focus: carouselThumbnails.value.length > 4 ? 'center' : 0,
  };
});

watch(isMD, () => {
  refresh();
});

watch(thumbnailsData, () => {
  refresh(); // this should only happen in the showroom
});

onMounted(() => {
  isMounted.value = true;
  if (!carousel.value || !thumbnails.value) {
    return;
  }

  syncCarousels();
  setTimeout(() => thumbnails.value?.refresh(), 500); // this is unfortunately required for the youtube-icon to show up
});

const notifySwipeToCatalogSlides = (index: number) => {
  catalogItem.value?.forEach((item) => item.onSwipe && item.onSwipe(index));
};

const notifySwipeToZoomCatalogSlides = (index: number) => {
  zoomCatalogItem.value?.forEach((item) => item.onSwipe && item.onSwipe(index));
};

const destroyZoom = async () => {
  setTimeout(() => {
    if (zoom.value) {
      (zoom.value as any).hasBeenOpened = false;
    }
  }, 300);
};

const openZoom = async () => {
  zoom.value?.open();
  await nextTick();
  (carouselZoom.value as any)?.$refs.carousel.sync(
    (thumbnailsZoom.value as any)?.$refs.carousel.splide,
  );
  carouselZoom.value?.go(carousel.value?.getIndex() || 0);
};

const syncCarousels = () => {
  thumbnailCarouselRef.value = thumbnails.value;
  (carousel.value as any)?.$refs.carousel.sync((thumbnails.value as any)?.$refs.carousel.splide);
};

const refresh = async () => {
  refreshCount.value += 1;
  await nextTick();
  syncCarousels();
};
</script>

<style src="./catalog-slider-fi-unscoped" />

<style scoped src="./catalog-slider-fi" />
